/**
 * The ONE list of object keys whose value never leaves the process.
 *
 * ── Why one list ────────────────────────────────────────────────────────────
 * Two sinks decide this: core's logger (`redactSensitive` in
 * `packages/core/src/services/loggerService.ts`) and the shared Sentry
 * scrubber (`./sentry`, the `request.data` an SDK attaches to a 500). They used
 * to keep separate lists, and they had drifted: the logger redacted `ssn`,
 * `dateOfBirth`, `pin`, `cvv`, card and gift-card numbers, while Sentry's key
 * filter knew only credentials and bank numbers. So a 500 on
 * `POST /payroll/me/ssn` would have carried the SSN and date of birth to a
 * third party that the log line beside it had redacted. Both now call
 * {@link isSensitiveKey}, so they cannot disagree again.
 *
 * ── The rule ────────────────────────────────────────────────────────────────
 * A key is sensitive when, lower-cased and stripped of everything but letters
 * and digits, it CONTAINS any fragment below — the logger's case-insensitive
 * substring rule, made separator-blind so `api_key`, `api-key` and `apiKey`,
 * or `date_of_birth` and `dateOfBirth`, are one key. It over-matches by design
 * (`shippingAddress` contains `pin`): hiding a harmless field costs a little
 * debugging, missing a credential cannot be taken back. A short handful of keys
 * that would over-match as fragments (`pan`, the expiry names) are matched as
 * the WHOLE normalised key instead ({@link SENSITIVE_EXACT_KEYS}).
 *
 * ── Why this module has NO imports ──────────────────────────────────────────
 * Same reason as `./redaction` and `./giftCardNumberRedaction`: the Sentry
 * scrubber is loaded by config files at process boot and ships in browser
 * bundles, and core's logger reads it through `@order-up/analytics/sentry`.
 */

/** Matched as substrings of the normalised key (see {@link normalizeSensitiveKey}). */
export const SENSITIVE_KEY_FRAGMENTS: readonly string[] = Object.freeze([
  // Credentials. `pass` covers password, passcode, passphrase and the bare
  // `pass` of a mail transport's `auth: { user, pass }`.
  'pass',
  'secret',
  'token',
  'authorization',
  'apikey',
  'credential',
  'privatekey',
  // Card numbers, and gift-card numbers with their protected forms (spec
  // 2026-10-08 §2): an alias spends the card exactly like the primary, and the
  // hash / ciphertext are server-only by design.
  'cardnumber',
  'aliasnumber',
  'numberciphertext',
  'numberhmac',
  'aliashmac',
  'cvv',
  // Payment card data beyond the number's own key (SOC 2 R60, PCI-2): the
  // security code under its other names (`cvc`, `cvc2`, `cardSecurityCode`,
  // `securityCode`), the number under its long name, and magnetic-stripe /
  // track data (`track1Data`, `track2Data`, `trackData`, `magStripe`) —
  // sensitive authentication data even where the reader encrypted it.
  'cvc',
  'securitycode',
  'primaryaccountnumber',
  'trackdata',
  'track1',
  'track2',
  'magstripe',
  // POS and gift-card PINs (`pin`, `newPin`, `currentPin`).
  'pin',
  // Payroll and merchant-onboarding KYC. `dob` is the field POST
  // /payroll/me/ssn actually takes; `dateofbirth` the onboarding one.
  'ssn',
  'dob',
  'dateofbirth',
  'driverslicense',
  'federaltaxid',
  // Bank numbers (ACH capture moved to our own inputs, 2026-10-14).
  'accountnumber',
  'routingnumber',
]);

/**
 * Matched as the WHOLE normalised key, because as fragments they would hide
 * half the estate: `pan` is inside `company`, `span`, `panel` and `japan`;
 * `expiry` and `expiration` inside every session, token and hold lifetime.
 *
 * `pan` is the field the POS native keyed fields sent to `/payments/sale`
 * (`{ kind: 'Card', pan, expiration, cvv }`), and it was on neither list —
 * SOC 2 R60 / `docs/compliance/PCI_DSS_SCOPE.md` PCI-2. The expiry keys are
 * cardholder data whenever they travel with a card number.
 */
export const SENSITIVE_EXACT_KEYS: readonly string[] = Object.freeze([
  'pan',
  'cardpan',
  'accountpan',
  'rawpan',
  'fullpan',
  'expiration',
  'expirationdate',
  'expirationmonth',
  'expirationyear',
  'expiry',
  'expirydate',
  'expirymonth',
  'expiryyear',
  'expirymmyy',
  'expdate',
  'expmonth',
  'expyear',
  'cardexpiration',
  'cardexpiry',
]);

/** `Date_Of-Birth` → `dateofbirth`: lower case, letters and digits only. */
export function normalizeSensitiveKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** True when a value stored under `key` must be redacted wherever it is written. */
export function isSensitiveKey(key: string): boolean {
  const normalised = normalizeSensitiveKey(key);
  if (SENSITIVE_EXACT_KEYS.includes(normalised)) return true;
  for (const fragment of SENSITIVE_KEY_FRAGMENTS) {
    if (normalised.includes(fragment)) return true;
  }
  return false;
}
